import {
    Hide, KeyValuePair, Show, ThemeTokens,
    SkeletonMotion, OccupantDetails,
} from '@acp/core-ui';
import {
    Box,
    Text,
    VStack,
    Grid,
    GridItem,
    CardBody,
    Skeleton,
    Button,
    HStack,
    Image,
} from '@chakra-ui/react';
import { PropertyAccessIssuesData } from '@acp/shared';
import React from 'react';
import { brandIcons } from '@acp/core-ui/assets';
import { AccessIssuesTagCollection } from './access-issues-tag-collection';
import { ResolveActionButton } from '../buttons/resolve-action-button';
import { useTenancyDetails } from '../../../tenancy';
import { RowLayout } from './access-issue-row-layout';

const COLORED_BORDER_WIDTH = 6;
const CARD_BORDER_PADDING = 5;
const gap = 4;

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[`
            "address address actions"
            "occupant occupant occupant"
            "reason reason reason"
            "status status status"
        `,
            null,
            null,
            null,
            '"address occupant reason status actions"',
            ]}
            gridTemplateColumns={[
                '1fr 1fr',
                'min-content 1fr 1fr',
                null,
                '1fr 1fr 1fr',
                '21% 17% 25% 14% 1fr',
            ]}
            gap={gap}
            alignItems={{ base: 'center', xl: 'start' }}
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

function AccessIssueRowHeader() {
    return (
        <Box
            borderBottomWidth={1}
            px={CARD_BORDER_PADDING}
            pb={2}
            fontSize={{ base: 12, lg: 13 }}
            w="full"
            borderLeftWidth={COLORED_BORDER_WIDTH}
            borderLeftColor="transparent"
        >
            <GridWrapper>
                <GridItem area="address">Address</GridItem>
                <GridItem area="occupant">Occupants</GridItem>
                <GridItem area="reason">Reason</GridItem>
                <GridItem area="status">Status</GridItem>
                <GridItem area="actions">
                    <Text> </Text>
                </GridItem>
            </GridWrapper>
        </Box>
    );
}

export function AccessIssueRowSkeleton({
    gridWrapper = GridWrapper,
    showOccupants = true,
    showAddress = true,
    showTags = true,
    showType = false,
}: {
    showOccupants?: boolean;
    showTags?: boolean;
    showType?: boolean;
    showAddress?: boolean;
    gridWrapper?: React.ComponentType<{ children: React.ReactNode }>;
}) {
    return (
        <RowLayout>
            <CardBody as={gridWrapper} p={0} alignItems="center" justifyItems="center">
                {
                    showType && (
                        <GridItem as={VStack} gap={1} gridArea="type" mb={[gap, null, null, 0]} alignItems="start">
                            <Skeleton>
                                <Text>Access Issue</Text>
                            </Skeleton>
                        </GridItem>
                    )
                }

                {
                    showAddress && (
                        <VStack
                            alignItems="start"
                            gap={1}
                            w={52}
                            gridArea="address"
                            mb={[gap, null]}
                        >
                            <SkeletonMotion height={5} />
                            <SkeletonMotion height={5} />
                        </VStack>
                    )
                }

                {
                    showOccupants && (
                        <RowLayout.Column area="occupant">
                            <Skeleton>
                                <Text fontSize="small">John Doe and Jack Doe</Text>
                                <Text fontSize="small">& 1 more</Text>
                            </Skeleton>
                        </RowLayout.Column>
                    )
                }

                <RowLayout.Column area="reason">
                    <Skeleton>
                        <Text fontSize="xs">
                            Access issue Reason 1, Access issue Reason 2
                        </Text>
                    </Skeleton>
                </RowLayout.Column>

                {
                    showTags && (
                        <RowLayout.Column area="status">
                            <Skeleton>
                                <Text fontSize="xs">
                                    DI remcommeded actions
                                </Text>
                            </Skeleton>
                        </RowLayout.Column>
                    )
                }

                <RowLayout.Column area="actions">
                    <Skeleton>
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            color="secondary.25"
                            size="sm"
                        >
                            actions menu xx
                        </Button>
                    </Skeleton>
                </RowLayout.Column>
            </CardBody>
        </RowLayout>
    );
}

function AccessIssueRow({
    accessIssueItem,
    gridWrapper = GridWrapper,
    showOccupants = true,
    showAddress = true,
    showTags = true,
    showType = false,
}: {
    accessIssueItem: PropertyAccessIssuesData;
    showOccupants?: boolean;
    showTags?: boolean;
    showType?: boolean;
    showAddress?: boolean;
    gridWrapper?: React.ComponentType<{ children: React.ReactNode }>;
}) {
    const {
        compliancePropertyId,
        addressLine1,
        addressLine2,
        accessIssues,
        tags,
    } = accessIssueItem;
    const { problems } = accessIssues;
    const { data: tenancyDetails, isLoading } = useTenancyDetails(compliancePropertyId);

    if (tenancyDetails === undefined || isLoading) {
        return (
            <AccessIssueRowSkeleton
                gridWrapper={gridWrapper}
                showOccupants={showOccupants}
                showAddress={showAddress}
                showTags={showTags}
                showType={showType}
            />
        );
    }

    return (
        <RowLayout>
            <RowLayout.Body as={gridWrapper}>
                { showType && (
                    <GridItem as={HStack} gap={2} area="type" mb={[gap, null, null, 0]}>
                        <Image src={brandIcons.activeNoticeSmMuted} h="35px" />
                        <VStack gap={0} alignItems="start">
                            <Text fontWeight="medium">Access Issue</Text>
                        </VStack>
                    </GridItem>
                )}

                {
                    showAddress && (
                        <RowLayout.Column area="address">
                            <RowLayout.Address
                                propertyId={compliancePropertyId}
                                addressLine1={addressLine1}
                                addressLine2={addressLine2}
                                tags={tags}
                            />
                        </RowLayout.Column>
                    )
                }

                { showOccupants && (
                    <RowLayout.Column area="occupant">
                        <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                            <OccupantDetails data={tenancyDetails} />
                        </Show>
                        <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                            <KeyValuePair
                                label="Occupants"
                                align="flex-start"
                                spacing={0}
                            >
                                <OccupantDetails data={tenancyDetails} />
                            </KeyValuePair>
                        </Hide>
                    </RowLayout.Column>
                )}

                <RowLayout.Column area="reason">
                    <RowLayout.Reason problems={problems} />
                </RowLayout.Column>

                {
                    showTags && (
                        <RowLayout.Column area="status">
                            <AccessIssuesTagCollection
                                flexDir={{ base: 'row', xl: 'column' }}
                                flexGrow={1}
                                flexWrap={{ base: 'wrap', xl: 'nowrap' }}
                                maxW="100%"
                                tags={tags}
                            />
                        </RowLayout.Column>
                    )
                }

                <RowLayout.Column area="actions">
                    <ResolveActionButton
                        propertyId={compliancePropertyId}
                        tenancyId={tenancyDetails?.currentTenancy?.id}
                    />
                </RowLayout.Column>
            </RowLayout.Body>
        </RowLayout>
    );
}

export { AccessIssueRow, AccessIssueRowHeader };
